import React from 'react';
import { toast } from 'react-toastify';
import metadata from '../../metadata.json';

const MainForm = ({
  formData,
  errors,
  loading,
  emailStatus,
  forceVerifyButton,
  initiatedPayment,
  translations,
  onSubmit,
  onChange,
  onCheckEmail,
  isFormValid,
  scanType,
  handleForgetLicenseKey
}) => {
  const isEmailDomainMatching = () => {
    if (!formData.email || !formData.target) return false;
    
    const emailDomain = formData.email.split('@')[1]?.toLowerCase();
    const targetDomain = formData.target.toLowerCase().replace(/^www\./, '');
    const normalizedEmailDomain = emailDomain?.replace(/^www\./, '');
    return normalizedEmailDomain === targetDomain;
  };

  return (
    <div
      className="mx-auto shadow p-4 rounded-4 bg-white"
      style={{ maxWidth: "1200px" }}
    >
      <div className="text-center">
        <h4 className="fw-bold">{translations.formTitle}</h4>
        <p className="text-muted mb-0 mb-lg-3">{translations.formDescription}</p>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className="mb-1 mb-lg-3">
          <label className="form-label">{translations.languageLabel}</label>
          <select
            className="form-select py-2"
            name="language"
            value={formData.language}
            onChange={onChange}
          >
            <option value="eng">{translations.englishOption}</option>
            <option value="ja">{translations.japaneseOption}</option>
          </select>
        </div>

        <div className="row">
          <div className="col-md-6 mb-1 mb-lg-3 mb-md-0">
            <label className="form-label">{translations.firstNameLabel}</label>
            <input
              type="text"
              className={`form-control py-2 ${
                errors.firstName ? "is-invalid" : ""
              }`}
              name="firstName"
              value={formData.firstName}
              onChange={onChange}
              placeholder={translations.firstNamePlaceholder}
              style={{ boxShadow: "none" }}
            />
            {errors.firstName && (
              <div className="invalid-feedback">{errors.firstName}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">{translations.lastNameLabel}</label>
            <input
              type="text"
              className={`form-control py-2 ${
                errors.lastName ? "is-invalid" : ""
              }`}
              name="lastName"
              value={formData.lastName}
              onChange={onChange}
              placeholder={translations.lastNamePlaceholder}
              style={{ boxShadow: "none" }}
            />
            {errors.lastName && (
              <div className="invalid-feedback">{errors.lastName}</div>
            )}
          </div>
        </div>

        <div className="mb-1 mb-lg-3 position-relative">
          <label className="form-label">
            {translations.emailLabel}&nbsp;
            <span className="text-muted small">{translations.emailDescription}</span>
          </label>
          <input
            type="email"
            className={`form-control py-2 pe-5 ${
              errors.email ? "is-invalid" : ""
            }`}
            name="email"
            value={formData.email}
            onChange={onChange}
            onBlur={onCheckEmail}
            onAnimationStart={(e) => {
              if (
                e.animationName.includes("auto-fill") ||
                e.animationName.includes("onAutoFillStart")
              ) {
                onCheckEmail({ target: { value: formData.email } });
              }
            }}
            onInput={(e) => {
              if (e.target.value && e.target.value !== formData.email) {
                onCheckEmail({ target: { value: e.target.value } });
              }
            }}
            placeholder={translations.emailPlaceholder}
            style={{ boxShadow: "none" }}
          />
          {emailStatus === "verified" && (
            <div
              className="position-absolute verified_icons end-0 d-flex align-items-center me-2 text-success"
              style={{ fontSize: "12px" }}
            >
              <span className="me-1">✔</span> {translations.emailVerified}
            </div>
          )}
          {emailStatus === "unverified" && (
            <div
              className="position-absolute verified_icons end-0 d-flex align-items-center me-2 text-danger"
              style={{ fontSize: "12px" }}
            >
              <span className="me-1">✖</span> {translations.emailNotVerified}
            </div>
          )}
          {errors.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
        </div>

        <div className="">
          <label className="form-label">{translations.domainLabel}</label>
          <div className="input-group">
            <span className="input-group-text">https://</span>
            <input
              type="text"
              className={`form-control py-2 ${
                errors.target ? "is-invalid" : ""
              }`}
              name="target"
              value={formData.target}
              onChange={onChange}
              placeholder={translations.domainPlaceholder}
              style={{ boxShadow: "none" }}
            />
          </div>
          {errors.target && (
            <div className="invalid-feedback">{errors.target}</div>
          )}
          <small className="text-muted d-block mt-1 domain_name">
            {translations.domainDescription}
          </small>
          {formData.email && formData.target && (
            <div className={`mt-1 small ${isEmailDomainMatching() ? 'text-success' : 'text-warning'}`}>
              {isEmailDomainMatching() ? (
                <><span className="me-1">✔</span> {translations.emailDomainMatches}</>
              ) : (
                <><span className="me-1">⚠</span> {translations.emailDomainDoesNotMatch}</>
              )}
            </div>
          )}
        </div>

        {scanType === "license" && emailStatus === "verified" && (
          <div className="mb-1 mb-lg-3">
            <label className="form-label">License Key <span className='text-danger'>*</span></label>
            <input
              type="text"
              className={`form-control py-2 ${
                errors.licenseId ? "is-invalid" : ""
              }`}
              name="licenseId"
              value={formData.licenseId}
              onChange={onChange}
              placeholder="Enter License key"
              style={{ boxShadow: "none" }}
            />
            {errors.licenseId && (
              <div className="invalid-feedback">{errors.licenseId}</div>
            )}
            <div className='d-flex justify-content-end align-items-end' onClick={() => {
              if (handleForgetLicenseKey) {
                handleForgetLicenseKey();
              }
            }}>
              <a className='text-primary' style={{ cursor: 'pointer' }}>Forgot License Key</a>
            </div>
          </div>
        )}

        <div className="form-check d-flex align-items-center mb-3">
          <input
            className={`form-check-input ${
              errors.agree ? "is-invalid" : ""
            }`}
            type="checkbox"
            name="agree"
            checked={formData.agree}
            onChange={onChange}
            id="tos"
            style={{ boxShadow: "none" }}
          />
          <label className="form-check-label ms-2 terms-label" htmlFor="tos">
            {translations.termsAgreement}{" "}
            <a
              href={`/termsofservice${window.location.search}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {translations.termsOfService}
            </a>{" "}
            {translations.termsAgreementEnd}
          </label>
          {errors.agree && (
            <div className="invalid-feedback d-block">{errors.agree}</div>
          )}
        </div>

        <button
          type="submit"
          className="btn w-100 text-white px-3 py-2 rounded-4 fw-bold"
          style={{
            background: "linear-gradient(to right, #2563eb, #4338ca)",
            opacity: !isFormValid() ? 0.7 : 1,
          }}
          disabled={loading || !isFormValid()}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              {translations.submitting}
            </>
          ) : forceVerifyButton ? (
            <>
              <i className="bi bi-envelope-exclamation-fill me-2" />
              {translations.verifyEmail}
            </>
          ) : emailStatus === "verified" && !initiatedPayment ? (
            <>
              <i className={scanType === "license" ? "bi bi-file-earmark-text-fill me-2" : "bi bi-credit-card-fill me-2"} />
              {scanType === "license" ? "Scan Report" : translations.initializePayment}
            </>
          ) : emailStatus === "verified" && initiatedPayment ? (
            <>
              <i className="bi bi-shield-lock-fill me-2" />
              {translations.startSecurityAssessment || "Start Security Assessment"}
            </>
          ) : (
            <>
              <i className="bi bi-shield-lock-fill me-2" />
              {translations.startAssessment}
            </>
          )}
        </button>
      </form>

      <div
        className="alert d-flex justify-content-end align-items-center rounded-2 mt-3 px-3 py-1"
        style={{
          backgroundColor: "rgb(239 246 255)",
          border: "1px solid rgb(191 219 254)",
        }}
      >
        <p
          className="mb-0 version"
        >
          v{metadata.buildMajor}.{metadata.buildMinor}.
          {metadata.buildRevision}
        </p>
      </div>
    </div>
  );
};

export default MainForm;
